import { useTranslation } from 'react-i18next';
import { Redirect, useLocation } from 'react-router-dom';
import { useAuthState } from 'state/useAuthState';
import { Divider, Typography } from '@mui/material';
import Helmet from 'react-helmet';
import { useExternalFeatureLayers } from 'features/mapping/hooks/useExternalFeatureLayers';
import { getPageStatus, PageLayout } from 'components/PageLayout';
import { SimplePageLayout } from 'components/layouts/SimplePageLayout';
import { OrganizationLayersList } from 'features/organizations/components/OrganizationLayersList/OrganizationLayersList';
import { ExternalFeatureLayer } from 'features/mapping/mapping.types';

export const filterLayers = (
  layers: ExternalFeatureLayer[],
  type: 'published' | 'unpublished',
): ExternalFeatureLayer[] => {
  if (type === 'published') {
    return layers.filter((layer) => layer.includeInLayerList);
  }
  return layers.filter(
    (layer) => layer.includeInLayerListForAdmins && !layer.includeInLayerList,
  );
};

export const OrganizationLayers = (): JSX.Element => {
  const { t } = useTranslation();
  const {
    permissions: { canManageOrganization },
  } = useAuthState();
  const location = useLocation<{ fromRoute?: string }>();
  const {
    externalFeatureLayers,
    isLoading: isLoadingExternalFeatureLayers,
    isError: isErrorExternalFeatureLayers,
  } = useExternalFeatureLayers();

  if (!canManageOrganization) {
    return <Redirect to="/" />;
  }

  const returnRoute = location.state?.fromRoute || '/';

  const status = getPageStatus({
    isLoading: isLoadingExternalFeatureLayers,
    isError: isErrorExternalFeatureLayers,
  });

  return (
    <PageLayout
      data-cname="OrganizationLayer"
      status={status}
      headerBarProps={{
        title: t('organizationLayers.title'),
        action: returnRoute,
      }}
    >
      <>
        <Helmet>
          <title>{t('organizationLayers.title')}</title>
        </Helmet>
        <SimplePageLayout disableGutters>
          <Typography variant="h3" sx={{ p: 2 }}>
            {t('organizationLayers.published')}
          </Typography>
          <Divider />
          <OrganizationLayersList
            layers={filterLayers(externalFeatureLayers, 'published')}
          />
          <Typography variant="h3" sx={{ p: 2, pt: 6 }}>
            {t('organizationLayers.unpublished')}
          </Typography>
          <Divider />
          <OrganizationLayersList
            layers={filterLayers(externalFeatureLayers, 'unpublished')}
          />
        </SimplePageLayout>
      </>
    </PageLayout>
  );
};
